import React, {
  useEffect,
  useState,
  type ReactElement,
  useContext
} from 'react'
import {
  Box,
  Flex,
  Heading,
  ListItem,
  UnorderedList,
  Text
} from '@chakra-ui/react'
import { connection, RoomCode } from '../../renderer/roomConnector'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import { ErrorModal, ScreenSizeErrorModal } from '../../components/Modals'
import { activities, universalRoomOptions } from '../../activities'
import SideNav from '../../components/SideNav'
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar, Doughnut } from 'react-chartjs-2'

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  ChartDataLabels
)

interface itemData {
  emoji: string
  color: string
  names: string[]
}
let isConnecting = false

// const CHANNEL_GLOBAL: any = null

const initialRoomSettings = {
  activity: activities.vibeCheck,
  info: (
    <>
      <Heading size="sm" mb="0">
        Host
      </Heading>
      <UnorderedList>
        {activities.vibeCheck.hostInfo.map((text: string, index: number) => {
          return <ListItem key={`hostInfo-${index}`}>{text}</ListItem>
        })}
      </UnorderedList>
      <Heading size="sm" mb="0" mt="20px">
        Audience
      </Heading>
      <UnorderedList>
        {activities.vibeCheck.audienceInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`audienceInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
    </>
  ),
  options: {
    ...universalRoomOptions,
    viewType: {
      label: 'View Type',
      type: 'select',
      value: 'wheel',
      options: [
        {
          label: 'Wheel',
          value: 'wheel'
        },
        {
          label: 'Donut Chart',
          value: 'donut'
        },
        {
          label: 'Bar Chart',
          value: 'bar'
        }
      ]
    },
    leakyBucket: {
      label: 'Reduce Emoji count by 1 every {X} second',
      type: 'inlineNumberWithToggle',
      active: false,
      value: 1,
      min: 1,
      max: 60,
      step: 1
    },
    staleEmoji: {
      label: 'Remove Emojis that are older than {X} second',
      type: 'inlineNumberWithToggle',
      active: false,
      value: 60,
      min: 10,
      max: 600,
      step: 5
    },
    maxEmojiCount: {
      label: 'Set A Max Emoji Count of {X}',
      type: 'inlineNumberWithToggle',
      active: false,
      value: 50,
      min: 10,
      max: 600,
      step: 5
    },
    showEmptyBucket: {
      label: 'Show "Empty Bucket" Emojis',
      type: 'bool',
      value: true
    }
  }
}

const VibeCheckLogo = (): ReactElement => {
  return (
    <Box
      mb="5.5vh"
      borderRadius="10vw"
      pos="absolute"
      color="#ED574F"
      letterSpacing="-0.05em"
      fontWeight={800}
      fontSize="4vw"
      animation="swing ease-in-out 3s infinite alternate"
      sx={{
        '&:before': {
          WebkitTextStrokeWidth: '0.03em',
          WebkitTextStrokeColor: '000000aa',
          content: '"Vibe Check"',
          position: 'absolute',
          top: '0',
          left: '0',
          zIndex: '-1'
        },
        '&:after': {
          content: '"Vibe Check"',
          position: 'absolute',
          top: '0.125em',
          left: '0',
          zIndex: '-1',
          textShadow: 'none',
          transition: 'top 250ms ease',
          backgroundImage: `
        linear-gradient(
          45deg,
          transparent 45%,
          #000000aa 45%,
          #000000aa 55%,
          transparent 0
          )`,
          backgroundSize: '.05em .05em',
          backgroundClip: 'text',
          textFillColor: 'transparent',

          animation: 'shad-anim 25s linear infinite'
        }
      }}
    >
      Vibe Check
    </Box>
  )
}

let orbitInterval: any
let leakyBucketInterval: any
let staleEmojiInterval: any

// const isDevMode = import.meta.env.MODE === 'development'
const isDevMode = import.meta.env.MODE === 'development'
const Page: React.FC = () => {
  const [roomCode, setRoomCode] = useState('')
  const { roomSettings, setRoomSettings } = useContext(RoomSettingsContext)
  const [emojis, setEmojis] = useState<itemData[]>([])
  const [hideView, setHideView] = useState<boolean>(false)
  const [globalRotation, setGlobalRotation] = useState<number>(0)
  // const [csvData, setCsvData] = useState<string[][]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const updateItems = (message: { emoji: string; clientId: string }): void => {
    const timeStamp = Date.now().valueOf()
    setEmojis((prevValue) => {
      const tempPrevVal = [...prevValue]
      const currentEmojiIndex = tempPrevVal.findIndex(
        (e) => e.emoji === message.emoji
      )

      if (currentEmojiIndex >= 0) {
        tempPrevVal[currentEmojiIndex].names.push(
          message.clientId + '-' + timeStamp.toString()
        )
        tempPrevVal[currentEmojiIndex].names = [
          ...new Set(tempPrevVal[currentEmojiIndex].names)
        ]
        if (roomSettings?.options.maxEmojiCount.active === true) {
          tempPrevVal[currentEmojiIndex].names.slice(
            0,
            roomSettings?.options.maxEmojiCount.value
          )
        }
      } else {
        tempPrevVal.push({
          names: [message.clientId],
          color: '',
          emoji: message.emoji
        })
      }
      return tempPrevVal
    })
  }

  const triggerAction = (message: any): void => {
    updateItems(message)
  }

  const triggerEnter = (message: any): void => {
    setHideView(false)
  }

  const VCCallback = (results: any): void => {
    if (results !== false) {
      setRoomCode(results.roomCode)
    } else {
      setErrorMessage('Failed to connect, try refreshing the window')
    }
  }
  useEffect(() => {
    setRoomSettings(initialRoomSettings)
  }, [])

  useEffect(() => {
    if (!isConnecting && roomSettings !== null) {
      isConnecting = true
      clearInterval(orbitInterval)
      orbitInterval = setInterval(() => {
        setGlobalRotation((prevVal) => prevVal + 0.5)
      }, 100)
      if (isDevMode) {
        setInterval(() => {
          setEmojis((prevValue) => {
            const newValue = [...prevValue]
            if (
              (Math.random() > 0.8 || newValue.length === 0) &&
              tempEmojis.length > newValue.length
            ) {
              newValue.push(tempEmojis[newValue.length])
            } else {
              const tempIndex = Math.floor(Math.random() * newValue.length)
              if (
                roomSettings?.options.maxEmojiCount.active === false ||
                newValue[tempIndex].names.length <
                  roomSettings?.options.maxEmojiCount.value
              ) {
                newValue[tempIndex].names.push(
                  'test-' + Date.now().valueOf().toString()
                )
              }

              // if (roomSettings?.options.maxEmojiCount.active === true) {
              //   newValue[tempIndex].names.slice(
              //     0,
              //     roomSettings?.options.maxEmojiCount.value
              //   )
              // }
            }
            return newValue
          })
        }, 250)
      }
      connection('vc', VCCallback, { triggerAction, triggerEnter })
    }
  }, [roomSettings])

  useEffect(() => {
    clearInterval(leakyBucketInterval)
    if (roomSettings?.options.leakyBucket.active === true) {
      leakyBucketInterval = setInterval(
        () => {
          setEmojis((prevValue) => {
            const newValue = [...prevValue]
            newValue.forEach((emoji) => {
              emoji.names.shift()
            })
            return newValue
          })
        },
        roomSettings?.options.leakyBucket.value * 1000
      )
    }
    clearInterval(staleEmojiInterval)
    staleEmojiInterval = setInterval(
      () => {
        setEmojis((prevValue) => {
          const newValue = [...prevValue]
          if (roomSettings?.options.staleEmoji.active === true) {
            newValue.forEach((emoji) => {
              emoji.names = emoji.names.filter((name) => {
                const time = parseInt(name.split('-')[1])
                const now = new Date().getTime()
                const dif = Math.abs(time - now) / 1000
                return dif <= roomSettings?.options.staleEmoji.value
              })
            })
          }
          return newValue
        })
      },
      roomSettings?.options.leakyBucket.value * 1000
    )
    if (roomSettings?.options.maxEmojiCount.active === true) {
      setEmojis((prevValue) => {
        const newValue = [...prevValue]

        newValue.forEach((emoji) => {
          emoji.names = emoji.names.slice(
            0,
            roomSettings?.options.maxEmojiCount.value
          )
        })
        return newValue
      })
    }
  }, [roomSettings])

  // useEffect(() => {
  //   const newCSVData = [['id', 'name', 'emoji']]
  //   emojis.forEach((item) => {
  //     newCSVData.push([item.id, item.name, item.emoji])
  //   })
  //   setCsvData(newCSVData)
  // }, [emojis])

  const filteredEmojis = emojis.filter((emoji) => {
    return (
      roomSettings?.options.showEmptyBucket.value === true ||
      emoji.names.length > 0
    )
  })

  const emojisDonutData = {
    labels: filteredEmojis.map((item) => item.emoji),
    datasets: [
      {
        data: filteredEmojis.map((item) => item.names.length),
        backgroundColor: pastelColors
      }
    ]
  }

  const sortedEmojis = [...filteredEmojis]
    .map((item, index) => {
      item.color = pastelColors[index]
      return item
    })
    .sort((a, b) => {
      return b.names.length - a.names.length
    })

  const emojisBarData = {
    labels: sortedEmojis.map((item) => item.emoji),
    datasets: [
      {
        data: sortedEmojis.map((item) => item.names.length),
        backgroundColor: sortedEmojis.map((item) => item.color)
      }
    ]
  }

  const emojiSize =
    filteredEmojis.length > 5
      ? filteredEmojis.length > 12
        ? 10 - filteredEmojis.length * 0.2
        : 10 - filteredEmojis.length * 0.2
      : 10

  return (
    <Box sx={styles.container}>
      {/* Wheel View Screen */}
      <Flex
        pos="absolute"
        left="0"
        top="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
        transition="all 500ms ease"
        opacity={hideView ? '0' : '1'}
        display={
          roomSettings?.options?.viewType.value === 'wheel' ? 'flex' : 'none'
        }
      >
        {filteredEmojis.map((item: itemData, index: number): ReactElement => {
          const rotation =
            (360 / filteredEmojis.length) * index + globalRotation
          const transformOrigin =
            filteredEmojis.length > 12 ? (index % 2 === 0 ? 41 : 30) : 41
          const top =
            filteredEmojis.length > 12 ? (index % 2 === 0 ? 0 : 5.5) : 0
          const fontSize = filteredEmojis.length > 12 ? 2.8 : 3.5
          const counterSize: number = Math.min(
            1.5,
            1 + item.names.length * 0.0125
          )
          const counterOffset = `calc((50% - ${counterSize}vw) - ${
            (fontSize + counterSize) * 0.5
          }vw)`

          return (
            <Box
              w={`${emojiSize}vw`}
              h={`${emojiSize}vw`}
              left={`calc(50% - ${emojiSize / 2}vw)`}
              top={`calc(${top}vw + 30px)`}
              key={item === null ? `placeholder-${index}` : item.emoji}
              position="absolute"
              color={'currentcolor'}
              style={{
                transform: `rotate(${rotation}deg)`
              }}
              transition="all 200ms linear"
              transformOrigin={`50% calc(${transformOrigin}vh - 0vw)`}
              borderRadius={`${emojiSize}vw`}
            >
              {item !== null && (
                <Flex
                  w={`${emojiSize}vw`}
                  h={`${emojiSize}vw`}
                  borderRadius={`${emojiSize}vw`}
                  style={{
                    transform: `rotate(${0 - rotation}deg)`
                  }}
                  transition="all 200ms linear"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text
                    borderRadius="10vw"
                    minW="1vw"
                    minH="1vw"
                    fontSize={`min(2vw, ${
                      item.names.length < 100
                        ? counterSize
                        : (counterSize * 2) / 3
                    }vw)`}
                    h={`${counterSize * 2}vw`}
                    w={`${counterSize * 2}vw`}
                    bg="red.300"
                    color="white"
                    textAlign="center"
                    lineHeight={`${2 * counterSize}vw`}
                    pos="absolute"
                    bottom={counterOffset}
                    right={counterOffset}
                    fontWeight="800"
                  >
                    {item.names.length}
                  </Text>
                  <Text
                    fontSize={`${fontSize}vw`}
                    textAlign="center"
                    fontWeight={800}
                    letterSpacing="-0.1em"
                    position="relative"
                  >
                    {item.emoji}
                  </Text>
                </Flex>
              )}
            </Box>
          )
        })}
        <VibeCheckLogo />
      </Flex>
      {/* end wheel view */}
      {/* Donut View Screen */}
      <Flex
        pos="absolute"
        left="0"
        top="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
        transition="all 500ms ease"
        opacity={hideView ? '0' : '1'}
        display={
          roomSettings?.options?.viewType.value === 'donut' ? 'flex' : 'none'
        }
      >
        <Box
          as={Doughnut}
          w="70vw"
          h="70vw"
          data={emojisDonutData}
          options={{
            plugins: {
              datalabels: {
                font: {
                  weight: 'bold',
                  size: '100%'
                },
                formatter: (content: any, args: any): any => {
                  const index = args.dataIndex
                  return args.chart.data.labels[index]
                }
              }
            }
          }}
        />
        <VibeCheckLogo />
      </Flex>
      {/* end donut view */}
      {/* Bar View Screen */}
      <Flex
        pos="absolute"
        left="0"
        top="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
        transition="all 500ms ease"
        opacity={hideView ? '0' : '1'}
        display={
          roomSettings?.options.viewType.value === 'bar' ? 'flex' : 'none'
        }
        pb="130px"
      >
        <Box
          as={Bar}
          data={emojisBarData}
          options={{
            plugins: {
              datalabels: {
                font: {
                  weight: 'bold',
                  size: '45%'
                },
                formatter: (content: any, args: any): any => {
                  const index = args.dataIndex
                  return args.chart.data.labels[index]
                }
              }
            },
            scales: {
              x: {
                display: false
              },
              y: {
                display: false
              }
            }
          }}
        />
      </Flex>
      {/* end donut view */}
      <ErrorModal
        isOpen={errorMessage !== null}
        onClose={() => {
          setErrorMessage(null)
        }}
      >
        {errorMessage}
      </ErrorModal>
      <ScreenSizeErrorModal />

      <RoomCode roomCode={roomCode} />
      <SideNav />
      <style>
        {`
          @keyframes swing {
            0% { transform: rotate(3deg); }
            100% { transform: rotate(-3deg); }
          }
          @keyframes shad-anim {
            0% {background-position: 0 0}
            0% {background-position: 100% -100%}
            }
          `}
      </style>
    </Box>
  )
}

const styles = {
  container: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  grid: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  csv: {
    position: 'fixed',
    bottom: '0.25em',
    left: '0.25em',
    fontSize: '25px',
    zIndex: 1000
  }
}

const pastelColors = [
  '#FFB6C1',
  '#FF69B4',
  '#FFA07A',
  '#FF6347',
  '#FFD700',
  '#FF8C00',
  '#FF4500',
  '#FFDAB9',
  '#FFE4B5',
  '#FFFFE0',
  '#FFE4E1',
  '#F0E68C',
  '#FAFAD2',
  '#90EE90',
  '#98FB98',
  '#ADFF2F',
  '#32CD32',
  '#00FF7F',
  '#00FA9A',
  '#00CED1',
  '#20B2AA',
  '#5F9EA0',
  '#B0E0E6',
  '#87CEEB',
  '#87CEFA',
  '#4682B4',
  '#6A5ACD',
  '#7B68EE',
  '#9370DB',
  '#8A2BE2',
  '#9400D3',
  '#9932CC',
  '#8B008B',
  '#800080',
  '#8B0000',
  '#A52A2A',
  '#CD5C5C',
  '#DC143C',
  '#FF0000',
  '#FF4500',
  '#FF6347',
  '#FF7F50',
  '#FF8C00',
  '#FFA07A',
  '#FFD700',
  '#FFDAB9',
  '#FFE4B5',
  '#FFFFE0',
  '#FFFACD',
  '#FFEFD5',
  '#FFE4E1',
  '#F5DEB3',
  '#FAF0E6',
  '#FFF5EE',
  '#FAEBD7',
  '#F0FFF0',
  '#E0FFFF',
  '#F0F8FF',
  '#F0E68C',
  '#FFFF00',
  '#FFD700',
  '#FFB6C1',
  '#FFA07A',
  '#FF69B4',
  '#DB7093',
  '#FFA500',
  '#FF4500',
  '#FF6347',
  '#FF8C00',
  '#FFDAB9',
  '#FFE4B5',
  '#FFFFE0',
  '#FFFACD',
  '#F0E68C',
  '#FAFAD2',
  '#90EE90',
  '#98FB98',
  '#ADFF2F',
  '#32CD32',
  '#00FF7F',
  '#00FA9A',
  '#00CED1',
  '#20B2AA',
  '#5F9EA0',
  '#B0E0E6',
  '#87CEEB',
  '#87CEFA',
  '#4682B4',
  '#6A5ACD',
  '#7B68EE',
  '#9370DB',
  '#8A2BE2',
  '#9400D3',
  '#9932CC',
  '#8B008B',
  '#800080',
  '#8B0000',
  '#A52A2A',
  '#CD5C5C',
  '#DC143C',
  '#FF0000',
  '#FF4500',
  '#FF6347',
  '#FF7F50',
  '#FF8C00'
]

const tempEmojis = [
  {
    emoji: '💯',
    color: '',
    names: []
  },
  {
    emoji: '❤',
    color: '',
    names: []
  },
  {
    emoji: '🌟',
    color: '',
    names: []
  },
  {
    emoji: '🍕',
    color: '',
    names: []
  },
  {
    emoji: '🌺',
    color: '',
    names: []
  },
  {
    emoji: '🎈',
    color: '',
    names: []
  },
  {
    emoji: '🐶',
    color: '',
    names: []
  },
  {
    emoji: '🍦',
    color: '',
    names: []
  },
  {
    emoji: '🎸',
    color: '',
    names: []
  },
  {
    emoji: '📚',
    color: '',
    names: []
  },
  {
    emoji: '🌍',
    color: '',
    names: []
  },
  {
    emoji: '🎨',
    color: '',
    names: []
  },
  {
    emoji: '🧘',
    color: '',
    names: []
  },
  {
    emoji: '🚀',
    color: '',
    names: []
  },
  {
    emoji: '🍔',
    color: '',
    names: []
  },
  {
    emoji: 'x',
    color: '',
    names: []
  },
  {
    emoji: 'c',
    color: '',
    names: []
  },
  {
    emoji: 'v',
    color: '',
    names: []
  },
  {
    emoji: 'b',
    color: '',
    names: []
  },
  {
    emoji: 'n',
    color: '',
    names: []
  },
  {
    emoji: 'm',
    color: '',
    names: []
  },
  {
    emoji: 'k',
    color: '',
    names: []
  },
  {
    emoji: 'j',
    color: '',
    names: []
  },
  {
    emoji: 'h',
    color: '',
    names: []
  },
  {
    emoji: 'g',
    color: '',
    names: []
  },
  {
    emoji: 'f',
    color: '',
    names: []
  },
  {
    emoji: 'd',
    color: '',
    names: []
  },
  {
    emoji: 's',
    color: '',
    names: []
  },
  {
    emoji: 'a',
    color: '',
    names: []
  },
  {
    emoji: 'e',
    color: '',
    names: []
  }
]

export { Page }
